import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Camera, ChevronLeft, ChevronRight, Clock3, ImageOff, Search, X } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import "./CSS/TimeLine.css";

const API_BASE = "https://timetrakerapi.sntservices.in";

interface Screenshot {
  screenshotId: number | null;
  screenshotUrl: string | null;
  time: string;
}
interface TimeBlock {
  timeRange: string;
  screenshots: Screenshot[];
}
interface TimelineData {
  selectedDate: string;
  selectedDayTotal: string;
  totalTimeThisWeek: string;
  totalTimeThisMonth: string;
  totalRecords: number;
  screenshotTimeBlocks: TimeBlock[];
}
interface PreviewShot {
  src: string;
  time: string;
  hour: string;
}
interface StaffOption {
  email: string;
  name?: string | null;
}

const screenshotSrc = (shot: Screenshot): string | null => {
  if (!shot.screenshotUrl) return null;
  const url = new URL(shot.screenshotUrl, API_BASE);
  url.searchParams.set("access_token", localStorage.getItem("token") || "");
  return url.toString();
};

const readableTime = (value: string) => {
  const match = value?.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  const hour = Number(match[1]);
  return `${hour % 12 || 12}:${match[2]} ${hour < 12 ? "AM" : "PM"}`;
};

const readableHour = (range: string) => {
  const [start, end] = range.split(" - ");
  return end ? `${readableTime(start)} – ${readableTime(end)}` : range;
};

const readableDuration = (value?: string) => {
  const match = value?.match(/^(\d+):(\d{1,2})/);
  if (!match) return "—";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const Timeline: React.FC = () => {
  const { user } = useUser();
  const adminId = user?.id;
  const [searchParams] = useSearchParams();
  const linkedEmail = searchParams.get("email") || "";
  const linkedName = searchParams.get("name") || "";
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(linkedEmail);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [appliedEmail, setAppliedEmail] = useState("");
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!adminId) return;
    const controller = new AbortController();
    setEmailLoading(true);
    axios.get<StaffOption[]>(`${API_BASE}/GetUser?Adminid=${adminId}`, { signal: controller.signal })
      .then((response) => setStaff(Array.from(new Map((response.data || []).filter((item) => item.email).map((item) => [item.email.toLowerCase(), item])).values())))
      .catch((err) => { if (!axios.isCancel(err)) setError("Unable to load employees. Please try again later."); })
      .finally(() => { if (!controller.signal.aborted) setEmailLoading(false); });
    return () => controller.abort();
  }, [adminId]);

  // Group by each capture's clock hour, including responses from older APIs
  // that still return 20-minute blocks.
  const blocks = useMemo<TimeBlock[]>(() => {
    const byHour = new Map<number, Screenshot[]>();
    for (const block of timelineData?.screenshotTimeBlocks || []) {
      for (const shot of block.screenshots || []) {
        const hour = Number(shot.time?.split(":")[0]);
        if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
        byHour.set(hour, [...(byHour.get(hour) || []), shot]);
      }
    }
    return Array.from(byHour.entries()).sort(([a], [b]) => a - b).map(([hour, shots]) => ({
      timeRange: `${String(hour).padStart(2, "0")}:00:00 - ${String((hour + 1) % 24).padStart(2, "0")}:00:00`,
      screenshots: shots.sort((a, b) => a.time.localeCompare(b.time)),
    }));
  }, [timelineData]);
  const previewShots = useMemo<PreviewShot[]>(() => blocks.flatMap((block) =>
    (block.screenshots || []).flatMap((shot) => {
      const src = screenshotSrc(shot);
      return src ? [{ src, time: shot.time, hour: readableHour(block.timeRange) }] : [];
    })
  ), [blocks]);
  const activePreview = previewIndex == null ? null : previewShots[previewIndex];
  const staffOptions = linkedEmail && !staff.some((person) => person.email.toLowerCase() === linkedEmail.toLowerCase())
    ? [{ email: linkedEmail, name: linkedName }, ...staff] : staff;
  const appliedStaff = staffOptions.find((person) => person.email.toLowerCase() === appliedEmail.toLowerCase());

  useEffect(() => {
    if (previewIndex == null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPreviewIndex(null);
      if (event.key === "ArrowLeft") setPreviewIndex((index) => index == null ? null : Math.max(0, index - 1));
      if (event.key === "ArrowRight") setPreviewIndex((index) => index == null ? null : Math.min(previewShots.length - 1, index + 1));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewIndex, previewShots.length]);

  const fetchTimeline = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!selectedEmail) { setError("Select an employee to view their timeline."); return; }
    if (!adminId) { setError("Your manager account could not be identified. Please sign in again."); return; }
    setLoading(true);
    setError("");
    setTimelineData(null);
    setPreviewIndex(null);
    try {
      const response = await axios.get<TimelineData>(`${API_BASE}/GetTimeline`, {
        params: { adminId, email: selectedEmail, selectedDate: format(selectedDate, "yyyy-MM-dd") },
      });
      setTimelineData(response.data);
      setAppliedEmail(selectedEmail);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || "Unable to load timeline data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resultDate = timelineData?.selectedDate
    ? format(new Date(`${timelineData.selectedDate}T00:00:00`), "EEEE, MMMM d, yyyy") : "";

  return <main className="staff-timeline">
    <header className="staff-timeline-header">
      <div><span className="staff-timeline-kicker">ACTIVITY / TIMELINE</span><h1>Screenshot timeline</h1><p>Review captured work, hour by hour, for any team member.</p></div>
    </header>

    <form className="staff-timeline-filters" onSubmit={fetchTimeline}>
      <label><span>Team member</span><select value={selectedEmail} onChange={(event) => setSelectedEmail(event.target.value)} disabled={emailLoading}><option value="">{emailLoading ? "Loading employees…" : "Select an employee"}</option>{staffOptions.map((person) => <option key={person.email} value={person.email}>{person.name?.trim() || "Name unavailable"} — {person.email}</option>)}</select></label>
      <label><span>Date</span><span className="staff-timeline-date"><CalendarDays size={17} /><DatePicker selected={selectedDate} onChange={(date: Date | null) => { if (date) setSelectedDate(date); }} dateFormat="MMM d, yyyy" maxDate={new Date()} /></span></label>
      <button type="submit" disabled={loading || emailLoading}><Search size={17} /> {loading ? "Loading…" : "Show timeline"}</button>
    </form>
    {error && <div className="staff-timeline-alert" role="alert">{error}<button type="button" onClick={() => setError("")} aria-label="Dismiss error"><X size={17} /></button></div>}

    {loading ? <div className="staff-timeline-placeholder"><span className="staff-timeline-loader" /> Loading hourly activity…</div> : timelineData ? <>
      <section className="staff-timeline-summary" aria-label="Selected employee time summary">
        <div className="staff-timeline-summary-intro"><span>VIEWING ACTIVITY FOR</span><strong>{appliedStaff?.name?.trim() || "Name unavailable"}</strong><small>{appliedEmail}</small><small>{resultDate}</small></div>
        <div className="staff-timeline-summary-metric"><Clock3 size={18} /><span>Selected day</span><strong>{readableDuration(timelineData.selectedDayTotal)}</strong></div>
        <div className="staff-timeline-summary-metric"><CalendarDays size={18} /><span>This week</span><strong>{readableDuration(timelineData.totalTimeThisWeek)}</strong></div>
        <div className="staff-timeline-summary-metric"><CalendarDays size={18} /><span>This month</span><strong>{readableDuration(timelineData.totalTimeThisMonth)}</strong></div>
      </section>

      <section className="staff-timeline-gallery" aria-labelledby="staff-timeline-gallery-title">
        <div className="staff-timeline-gallery-header"><div><span className="staff-timeline-kicker">HOURLY REVIEW</span><h2 id="staff-timeline-gallery-title">Screenshot activity</h2><p>Captures are grouped by the hour of their recorded time.</p></div><div className="staff-timeline-count"><Camera size={17} /> {previewShots.length} {previewShots.length === 1 ? "screenshot" : "screenshots"} <span>· {timelineData.totalRecords || 0} entries</span></div></div>
        {blocks.length ? <div className="staff-timeline-hours">{blocks.map((block, blockIndex) => {
          const captures = (block.screenshots || []).filter((shot) => shot.screenshotUrl);
          const missing = (block.screenshots || []).length - captures.length;
          return <div className="staff-timeline-hour" key={`${block.timeRange}-${blockIndex}`}>
            <div className="staff-timeline-hour-label"><span className="staff-timeline-hour-marker" /><strong>{readableHour(block.timeRange)}</strong><small>{captures.length} {captures.length === 1 ? "capture" : "captures"}{missing > 0 ? ` · ${missing} without image` : ""}</small></div>
            <div className="staff-timeline-hour-content">{captures.length ? <div className="staff-timeline-shot-grid">{captures.map((shot, index) => {
              const src = screenshotSrc(shot);
              const globalIndex = previewShots.findIndex((item) => item.src === src && item.time === shot.time);
              return <button className="staff-timeline-shot" type="button" key={shot.screenshotId ?? `${blockIndex}-${index}`} onClick={() => setPreviewIndex(globalIndex)} aria-label={`Open screenshot captured at ${readableTime(shot.time)}`}>
                <span className="staff-timeline-shot-image"><img src={src || ""} alt={`Screen captured at ${readableTime(shot.time)}`} loading="lazy" decoding="async" /></span>
                <span className="staff-timeline-shot-caption"><Clock3 size={14} /> {readableTime(shot.time)} <span>View full image</span></span>
              </button>;
            })}</div> : <div className="staff-timeline-no-capture"><ImageOff size={18} /> Time entries in this hour have no screenshots.</div>}</div>
          </div>;
        })}</div> : <div className="staff-timeline-empty"><ImageOff size={26} /><strong>No activity recorded on this date</strong><span>Choose another day or employee to review their screenshots.</span></div>}
      </section>
    </> : <div className="staff-timeline-placeholder staff-timeline-start"><Camera size={29} /><strong>Ready to review activity</strong><span>Select an employee and date, then open the timeline.</span></div>}

    {activePreview && <div className="staff-timeline-lightbox" role="presentation" onMouseDown={() => setPreviewIndex(null)}><div className="staff-timeline-lightbox-dialog" role="dialog" aria-modal="true" aria-label={`Screenshot ${previewIndex! + 1} of ${previewShots.length}`} onMouseDown={(event) => event.stopPropagation()}>
      <div className="staff-timeline-lightbox-header"><div><strong>{readableTime(activePreview.time)}</strong><span>{activePreview.hour} · {previewIndex! + 1} of {previewShots.length}</span></div><button type="button" onClick={() => setPreviewIndex(null)} aria-label="Close screenshot"><X size={21} /></button></div>
      <div className="staff-timeline-lightbox-image"><img src={activePreview.src} alt={`Screen captured at ${readableTime(activePreview.time)}`} /></div>
      {previewShots.length > 1 && <div className="staff-timeline-lightbox-nav"><button type="button" onClick={() => setPreviewIndex(Math.max(0, previewIndex! - 1))} disabled={previewIndex === 0}><ChevronLeft size={19} /> Previous</button><button type="button" onClick={() => setPreviewIndex(Math.min(previewShots.length - 1, previewIndex! + 1))} disabled={previewIndex === previewShots.length - 1}>Next <ChevronRight size={19} /></button></div>}
    </div></div>}
  </main>;
};

export default Timeline;
